import { isEqual } from 'lodash';
import React, {
  useReducer,
  useMemo,
  useCallback,
  useState,
  useContext
} from 'react';
import useTranslations from '../../hooks/useTranslations';
import Colors from '../../internal-components/Colors';
import generateId from '../../utils/generateId';
import CustomButtonWizardView from '../view/CustomButtonWizardView';
import {
  ACTIVE_VIEW,
  BUTTON_TYPE,
  CustomButtonsContext,
  SYSTEM_BUTTONS_DEFAULT_SCHEMA,
  INTEGRATION_BUTTONS_DEFAULT_SCHEMA
} from './FormBuilderCustomButtonsEngine';
import { FormBuilderContext } from './FormBuilderEngine';

export const getButtonStyle = (variant, color) => {
  const staticCSS = `transition: 0.5s !important;`;
  let dynamicCSS = '';

  if (variant === 'contained') {
    dynamicCSS = `background-color: ${color} !important;`;
  } else if (variant === 'outlined') {
    dynamicCSS = `color: ${color} !important; border: 1px solid ${color} !important;`;
  }

  return `${staticCSS} ${dynamicCSS}`;
};

const CustomButtonWizardReducer = (state, action) => {
  const { type, payload, index } = action;

  switch (type) {
    case 'update':
      return { ...state, ...payload };

    default:
      return state;
  }
};

const initialData = {
  id: undefined,
  label: '',
  variant: 'contained',
  color: Colors.PRIMARY,
  type: '',
  statuses: []
};

const CustomButtonWizardEngine = ({
  buttonData = initialData,
  save = buttonData => buttonData
}) => {
  const [data, dispatch] = useReducer(CustomButtonWizardReducer, buttonData);
  const { form } = useContext(FormBuilderContext);
  const { onWizardClosed } = useContext(CustomButtonsContext);

  const [isNoStatusesDialogOpen, setIsNoStatusesDialogOpen] = useState(false);
  const [isWizardDirtyDialogOpen, setIsWizardDirtyDialogOpen] = useState(false);
  const [isUseSaveNotSubmitChecked, setIsUseSaveNotSubmitChecked] = useState(
    buttonData.additionalParameters &&
      buttonData.additionalParameters.root &&
      buttonData.additionalParameters.root.action
  );
  const t = useTranslations();

  const isSystemButton = useMemo(
    () =>
      Object.values(SYSTEM_BUTTONS_DEFAULT_SCHEMA).find(
        sb => sb.id === buttonData.id
      ),
    [buttonData.id]
  );

  const isIntegrationButton = useMemo(
    () =>
      Object.values(INTEGRATION_BUTTONS_DEFAULT_SCHEMA).find(
        sb => sb.id === buttonData.id
      ),
    [buttonData.id]
  );

  const reasonsSaveIsDisabled = useMemo(() => {
    let reasons = [];

    if (!(data.label || '').trim()) {
      reasons.push(`${t('customButtons.wizard.error.missingLabel')}`);
    }
    if (!(data.type || '').trim()) {
      reasons.push(`${t('customButtons.wizard.error.missingType')}`);
    }

    return reasons.length
      ? t('customButtons.wizard.error.sentence', {
          reasons: reasons.join(', ')
        })
      : '';
  }, [data]);

  const onBackClicked = useCallback(() => {
    if (!isEqual(data, buttonData)) {
      setIsWizardDirtyDialogOpen(true);
      return;
    }

    onWizardClosed();
  }, [data]);

  const onSave = useCallback(
    forceSave => {
      if (!data.statuses.length && !forceSave) {
        setIsNoStatusesDialogOpen(true);
        return;
      }

      const id = data.id || generateId();
      const type = data.type || BUTTON_TYPE.ADMIN; // PHASE 1 - only suport admin side button customization
      save({ ...data, id, type });
      onWizardClosed();
    },
    [data, save]
  );

  const onStatusDDChange = useCallback(
    values => {
      const isAllSelected = data.statuses.includes('all');
      const formStatuses = form.customStatuses || [];
      let newValues = [];

      if (values.includes('all') && !isAllSelected) {
        newValues = ['all', 'new', ...formStatuses.map(s => s.internalId)];
      } else if (values.includes('all') && isAllSelected) {
        newValues = values.filter(s => s !== 'all');
      } else {
        newValues = values;
      }

      dispatch({ type: 'update', payload: { statuses: newValues } });
    },
    [data, form]
  );

  const statusesValues = useMemo(() => {
    if (data.statuses && data.statuses.includes('all')) {
      return [
        'all',
        'new',
        ...(form.customStatuses || []).map(cs => cs.internalId)
      ];
    }
    return data.statuses;
  }, [data, form]);

  const onUseSaveNotSubmitChecked = useCallback(isChecked => {
    setIsUseSaveNotSubmitChecked(isChecked);

    const additionalParameters = isChecked
      ? { root: { action: 'save' } }
      : undefined;

    dispatch({ type: 'update', payload: { additionalParameters } });
  }, []);

  const onTypeChange = useCallback(
    v => {
      dispatch({ type: 'update', payload: { type: v } });
      if (v === 'admin') {
        onUseSaveNotSubmitChecked({ target: { checked: false } });
      }
    },
    [onUseSaveNotSubmitChecked]
  );

  const viewProps = {
    ...data,
    label: t(data.label),
    statuses: statusesValues,
    dispatch,
    reasonsSaveIsDisabled,
    isNoStatusesDialogOpen,
    setIsNoStatusesDialogOpen,
    onSave,
    onBackClicked,
    setIsWizardDirtyDialogOpen,
    isWizardDirtyDialogOpen,
    onWizardClosed,
    onStatusDDChange,
    isIntegrationButton,
    isSystemButton,
    isUseSaveNotSubmitChecked,
    onUseSaveNotSubmitChecked,
    onTypeChange
  };

  return <CustomButtonWizardView {...viewProps} />;
};

export default CustomButtonWizardEngine;
